import { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { CompanyModuleEnum } from '@api/ringil-api.v2';
import { isNullOrUndefined } from '@utils/utils';

/**
 * What a reservation's location allows a *new* reservation to be created on (RD-2872/RD-2890).
 *
 * A location can be deleted in two ways: plainly deleted, or superseded — deleted with a replacement
 * location that takes over its bookings. Nothing new may be created on a plainly deleted location,
 * while a superseded one forwards to its replacement.
 */
export interface LocationSupersedeState {
  /** Both lookups finished. Until then no affordance should be hidden — the location looks usable. */
  resolved: boolean;
  /** The replacement, when the location was superseded. */
  replacement?: { id: string; name: string };
  /** Deleted with no replacement — nothing can be created on it any more. */
  deletedWithoutReplacement: boolean;
}

const INITIAL: LocationSupersedeState = { resolved: false, deletedWithoutReplacement: false };

/**
 * Resolves the state above for one location.
 *
 * `getOneCGLocationV2` carries the replacement but not the deleted flag, so a location without a
 * replacement takes a second lookup: the LOV, which returns `isDeleted` and — thanks to
 * `includeLocationId` — keeps returning the location even though it is filtered out of the offer.
 * Anything that fails to resolve is reported as usable: a failed lookup must not take an action away.
 */
export const useLocationSupersedeState = (locationId: string, customerId: string): LocationSupersedeState => {
  const { getOneCGLocationV2, lovGetAllLocationsForCompanyId } = useContext(ApiContext);
  const [state, setState] = useState<LocationSupersedeState>(INITIAL);

  useEffect(() => {
    if (isNullOrUndefined(locationId) || isNullOrUndefined(customerId)) {
      setState(INITIAL);
      return;
    }
    let cancelled = false;
    const resolve = (next: LocationSupersedeState) => {
      if (!cancelled) setState(next);
    };
    const usable = () => resolve({ resolved: true, deletedWithoutReplacement: false });

    const checkDeleted = () => {
      lovGetAllLocationsForCompanyId(
        '',
        false,
        false,
        locationId,
        CompanyModuleEnum.TIMESLOTS,
        customerId,
        d => {
          // A location the LOV does not return at all stays usable — the pinned id means it is missing
          // for a reason we cannot tell apart from a plain deletion, and hiding actions on a guess is worse.
          const found = d?.find(t => t.id === locationId);
          resolve({ resolved: true, deletedWithoutReplacement: found?.isDeleted === true });
        },
        null,
        { onError: () => usable() },
      );
    };

    setState(INITIAL);
    getOneCGLocationV2(
      locationId,
      data => {
        if (data?.supersededBy) {
          resolve({ resolved: true, deletedWithoutReplacement: false, replacement: { id: data.supersededBy.id, name: data.supersededBy.name } });
        } else {
          checkDeleted();
        }
      },
      null,
      {
        onEntityNotFound: () => resolve({ resolved: true, deletedWithoutReplacement: true }),
        onError: () => usable(),
      },
    );

    return () => {
      cancelled = true;
    };
  }, [locationId, customerId]);

  return state;
};
